import { useRef, useCallback } from "react";
import { ReactGanttRef, Task } from "@dhx/react-gantt";

export interface UseGanttProps {
  onTaskDblClick?: (task: Task) => void;
  onAddTask?: (parentTask: Task | null) => void;
  onTaskUpdate?: (task: Task, mode: string) => void;
  onProgressUpdate?: (task: Task) => void;
  onTaskReorder?: (items: { id: number; sequence_order: number }[]) => void;
  onLinkAdd?: (link: any) => void;
  onLinkDelete?: (linkId: number) => void;
}

export const useGantt = ({
  onTaskDblClick,
  onAddTask,
  onTaskUpdate,
  onProgressUpdate,
  onTaskReorder,
  onLinkAdd,
  onLinkDelete,
}: UseGanttProps) => {
  const ganttRef = useRef<ReactGanttRef>(null);

  const setZoom = useCallback((level: string) => {
    const ganttInstance = ganttRef.current?.instance;
    if (!ganttInstance) return;

    let scales: { unit: string; step: number; format: string }[] = [];
    let scale_height = 60;

    switch (level) {
      case "day":
        scales = [
          { unit: "month", step: 1, format: "%M %Y" },
          { unit: "day", step: 1, format: "%d %M" },
        ];
        break;
      case "week":
        scales = [
          { unit: "month", step: 1, format: "%M %Y" },
          { unit: "week", step: 1, format: "Wk #%W" },
        ];
        break;
      case "month":
        scales = [
          { unit: "year", step: 1, format: "%Y" },
          { unit: "month", step: 1, format: "%M %Y" },
        ];
        break;
      case "year":
        scales = [{ unit: "year", step: 1, format: "%Y" }];
        break;
      default:
        return;
    }

    ganttInstance.config.scales = scales as any;
    ganttInstance.config.scale_height = scale_height;
    ganttInstance.render();
  }, []);

  const expandAll = useCallback(() => {
    const ganttInstance = ganttRef.current?.instance;
    if (ganttInstance) {
      const action = () => {
        ganttInstance.eachTask((task: any) => {
          ganttInstance.open(task.id);
        });
      };

      if (ganttInstance.batchUpdate) {
        ganttInstance.batchUpdate(action);
      } else {
        action();
        ganttInstance.render();
      }
    }
  }, []);

  const collapseAll = useCallback(() => {
    const ganttInstance = ganttRef.current?.instance;
    if (ganttInstance) {
      const action = () => {
        ganttInstance.eachTask((task: any) => {
          ganttInstance.close(task.id);
        });
      };

      if (ganttInstance.batchUpdate) {
        ganttInstance.batchUpdate(action);
      } else {
        action();
        ganttInstance.render();
      }
    }
  }, []);

  const handleTaskDblClick = useCallback(
    (id: string | number) => {
      if (!onTaskDblClick) return false;

      const ganttInstance = ganttRef.current?.instance;
      if (ganttInstance) {
        const task = ganttInstance.getTask(id);
        if (task) {
          onTaskDblClick(task);
        }
      }
      return false;
    },
    [onTaskDblClick]
  );

  const handleBeforeTaskAdd = useCallback(
    (id: string | number, task: Task) => {
      if (!onAddTask) return false;

      const ganttInstance = ganttRef.current?.instance;
      if (ganttInstance) {
        const parentTask = task.parent
          ? ganttInstance.getTask(task.parent)
          : null;

        if (parentTask && parentTask.type === "milestone") {
          return false;
        }

        onAddTask(parentTask);

        setTimeout(() => {
          if (ganttInstance.isTaskExists(id)) {
            ganttInstance.deleteTask(id);
          }
        }, 0);
      }
      return false;
    },
    [onAddTask]
  );

  const handleAfterTaskDrag = useCallback(
    (id: string | number, mode: string) => {
      const ganttInstance = ganttRef.current?.instance;
      if (!ganttInstance) return true;

      const task = ganttInstance.getTask(id);
      if (!task) return true;

      if (mode === "progress" && onProgressUpdate) {
        onProgressUpdate(task);
        return true;
      }

      if ((mode === "resize" || mode === "move") && onTaskUpdate) {
        onTaskUpdate(task, mode);
      }

      return true;
    },
    [onProgressUpdate, onTaskUpdate]
  );

  const checkMoveValidity = useCallback(
    (
      task: Task,
      parentId: string | number | null | undefined,
      ganttInstance: any
    ) => {
      // Normalize parentId to check for root
      const isRoot =
        parentId === 0 ||
        parentId === "0" ||
        parentId === null ||
        parentId === undefined;

      if (task.type === "project") {
        // Phase: MUST be at root
        if (!isRoot) {
          ganttInstance.message({
            type: "error",
            text: "Phases must remain at the root level.",
          });
          return false;
        }
      } else {
        // Activity/StageGate

        // 1. Must not be at root
        if (isRoot) {
          ganttInstance.message({
            type: "error",
            text: "Activities/Stage Gates cannot be moved to the root level.",
          });
          return false;
        }

        // 2. Must NOT change parent (only reorder within same parent)
        // Note: task.parent is the *old* parent at this point (before drag end confirms)
        const oldParentId = task.parent;
        // Normalize comparisons
        const normalizedOld =
          oldParentId === 0 || oldParentId === "0" ? 0 : Number(oldParentId);
        const normalizedNew =
          parentId === 0 || parentId === "0" ? 0 : Number(parentId);

        if (normalizedOld !== normalizedNew) {
          ganttInstance.message({
            type: "error",
            text: "Activities cannot be moved to a different phase.",
          });
          return false;
        }
      }

      return true;
    },
    []
  );

  const handleRowDragEnd = useCallback(
    (id: string | number, parentId: string | number, index: number) => {
      const ganttInstance = ganttRef.current?.instance;
      if (!ganttInstance || !onTaskReorder) return;

      const task = ganttInstance.getTask(id);
      if (!task) return;

      const allUpdates: { id: number; sequence_order: number }[] = [];

      ganttInstance.eachTask((task: Task) => {
        const index = ganttInstance.getTaskIndex(task.id);
        allUpdates.push({
          id: Number(task.id),
          sequence_order: index + 1,
        });
      });

      if (allUpdates.length === 0) {
        console.warn("No tasks found to reorder.");
        return;
      }

      onTaskReorder(allUpdates);
    },
    [onTaskReorder]
  );

  const handleBeforeRowDragEnd = useCallback(
    (id: string | number, parentId: string | number, index: number) => {
      const ganttInstance = ganttRef.current?.instance;
      if (!ganttInstance) return true;

      const task = ganttInstance.getTask(id);
      if (!task) return true;

      const isValid = checkMoveValidity(task, parentId, ganttInstance);

      if (!isValid) {
        return false;
      }

      return true;
    },
    [checkMoveValidity]
  );

  const handleAfterLinkAdd = useCallback(
    (id: string | number, link: any) => {
      if (!onLinkAdd) return true;

      const ganttInstance = ganttRef.current?.instance;
      if (ganttInstance) {
        onLinkAdd(link);

        setTimeout(() => {
          if (ganttInstance.isLinkExists(id)) {
            ganttInstance.deleteLink(id);
          }
        }, 0);
      }
      return false;
    },
    [onLinkAdd]
  );

  const handleBeforeLinkDelete = useCallback(
    (id: string | number) => {
      if (!onLinkDelete) return true;

      onLinkDelete(Number(id));
      return false;
    },
    [onLinkDelete]
  );

  return {
    ganttRef,
    setZoom,
    expandAll,
    collapseAll,
    handleTaskDblClick,
    handleBeforeTaskAdd,
    handleAfterTaskDrag,
    handleRowDragEnd,
    handleBeforeRowDragEnd,
    handleAfterLinkAdd,
    handleBeforeLinkDelete,
  };
};
